import * as THREE from 'three';

/**
 * Handles pathfinding and navigation for game entities
 */
export class NavigationSystem {
  private path: THREE.Vector3[] = [];
  private currentPathIndex: number = 0;
  private pathCompleted: boolean = false;
  private targetPosition: THREE.Vector3 | null = null;
  private obstacles: THREE.Object3D[] = [];
  private navMesh: THREE.Mesh | null = null;
  
  /**
   * Creates a new NavigationSystem
   */
  constructor() {
    // Initialize with empty path
    this.resetPath();
  }
  
  /**
   * Sets the navigation mesh for pathfinding
   * @param mesh The navigation mesh
   */
  public setNavMesh(mesh: THREE.Mesh): void {
    this.navMesh = mesh;
  }
  
  /**
   * Adds an obstacle to avoid during pathfinding
   * @param obstacle The obstacle object
   */
  public addObstacle(obstacle: THREE.Object3D): void {
    this.obstacles.push(obstacle);
  }
  
  /**
   * Removes an obstacle
   * @param obstacle The obstacle to remove
   */
  public removeObstacle(obstacle: THREE.Object3D): void {
    const index = this.obstacles.indexOf(obstacle);
    if (index !== -1) {
      this.obstacles.splice(index, 1);
    }
  }
  
  /**
   * Calculates a path from start to target
   * @param start The starting position
   * @param target The target position
   */
  public calculatePath(start: THREE.Vector3, target: THREE.Vector3): void {
    // Reset current path
    this.resetPath();
    
    // Store target
    this.targetPosition = target.clone();
    
    // For now, we'll use a simple direct path
    // In a more complex implementation, this would use A* or similar algorithm
    this.path.push(start.clone());
    
    // Check if we need to navigate around obstacles
    if (this.obstacles.length > 0) {
      // Simple obstacle avoidance - add waypoints around obstacles
      // This is a very basic implementation
      for (const obstacle of this.obstacles) {
        if (this.isObstacleInPath(start, target, obstacle)) {
          const avoidancePoint = this.calculateAvoidancePoint(start, target, obstacle);
          if (avoidancePoint) {
            this.path.push(avoidancePoint);
          }
        }
      }
    }
    
    // Add target as final point
    this.path.push(target.clone());
    
    // Reset path index
    this.currentPathIndex = 0;
    this.pathCompleted = false;
  }
  
  /**
   * Checks if an obstacle is in the path
   * @param start The starting position
   * @param target The target position
   * @param obstacle The obstacle to check
   * @returns True if the obstacle is in the path
   */
  private isObstacleInPath(start: THREE.Vector3, target: THREE.Vector3, obstacle: THREE.Object3D): boolean {
    // Get obstacle position and approximate radius
    const obstaclePos = obstacle.position;
    
    // Calculate a simple bounding radius for the obstacle
    let obstacleRadius = 1.0; // Default radius
    
    // If the obstacle has a bounding sphere, use that
    if (obstacle instanceof THREE.Mesh && obstacle.geometry.boundingSphere) {
      obstacleRadius = obstacle.geometry.boundingSphere.radius * 
        Math.max(obstacle.scale.x, obstacle.scale.y, obstacle.scale.z);
    }
    
    // Create a ray from start to target
    const direction = new THREE.Vector3().subVectors(target, start).normalize();
    const ray = new THREE.Ray(start, direction);
    
    // Calculate distance from ray to obstacle center
    const closestPoint = new THREE.Vector3();
    ray.closestPointToPoint(obstaclePos, closestPoint);
    const distance = obstaclePos.distanceTo(closestPoint);
    
    // Check if the closest point is within the path segment
    const startToClosest = closestPoint.distanceTo(start);
    const startToTarget = target.distanceTo(start);
    
    // If the closest point is beyond the target, it's not in our path
    if (startToClosest > startToTarget) {
      return false;
    }
    
    // If the distance is less than the obstacle radius, the obstacle is in the path
    return distance < obstacleRadius + 0.5; // Add a small buffer
  }
  
  /**
   * Calculates a point to avoid an obstacle
   * @param start The starting position
   * @param target The target position
   * @param obstacle The obstacle to avoid
   * @returns A waypoint to avoid the obstacle
   */
  private calculateAvoidancePoint(
    start: THREE.Vector3, 
    target: THREE.Vector3, 
    obstacle: THREE.Object3D
  ): THREE.Vector3 | null {
    // Get obstacle position
    const obstaclePos = obstacle.position;
    
    // Calculate a simple bounding radius for the obstacle
    let obstacleRadius = 1.0; // Default radius
    
    // If the obstacle has a bounding sphere, use that
    if (obstacle instanceof THREE.Mesh && obstacle.geometry.boundingSphere) {
      obstacleRadius = obstacle.geometry.boundingSphere.radius * 
        Math.max(obstacle.scale.x, obstacle.scale.y, obstacle.scale.z);
    }
    
    // Add a buffer to the radius
    const avoidanceRadius = obstacleRadius + 1.0;
    
    // Calculate path direction
    const pathDirection = new THREE.Vector3().subVectors(target, start).normalize();
    
    // Calculate vector from obstacle to path
    const obstacleToPath = new THREE.Vector3();
    const ray = new THREE.Ray(start, pathDirection);
    ray.closestPointToPoint(obstaclePos, obstacleToPath);
    obstacleToPath.sub(obstaclePos).normalize();
    
    // If we're too close to the obstacle, move perpendicular to the path
    if (obstacleToPath.length() < 0.1) {
      // Create a perpendicular vector
      obstacleToPath.set(-pathDirection.z, 0, pathDirection.x).normalize();
    }
    
    // Calculate avoidance point
    const avoidancePoint = new THREE.Vector3().copy(obstaclePos).add(
      obstacleToPath.multiplyScalar(avoidanceRadius)
    );
    
    // Keep the y-coordinate the same as the start position (for ground movement)
    avoidancePoint.y = start.y;
    
    return avoidancePoint;
  }
  
  /**
   * Gets the next position to move towards
   * @param currentPosition The current position
   * @param moveDistance The maximum distance to move
   * @returns The next position to move towards, or null if the path is completed
   */
  public getNextPosition(currentPosition: THREE.Vector3, moveDistance: number): THREE.Vector3 | null {
    if (this.pathCompleted || this.path.length === 0) {
      return null;
    }
    
    // Get current waypoint
    const currentWaypoint = this.path[this.currentPathIndex];
    
    // Check if we've reached the current waypoint
    const distanceToWaypoint = currentPosition.distanceTo(currentWaypoint);
    if (distanceToWaypoint <= moveDistance) {
      // Move to next waypoint
      this.currentPathIndex++;
      
      // Check if we've completed the path
      if (this.currentPathIndex >= this.path.length) {
        this.pathCompleted = true;
        return null;
      }
      
      // Return the next waypoint
      return this.path[this.currentPathIndex];
    }
    
    // Calculate direction to current waypoint
    const direction = new THREE.Vector3().subVectors(currentWaypoint, currentPosition).normalize();
    
    // Calculate next position
    return new THREE.Vector3().copy(currentPosition).add(direction.multiplyScalar(moveDistance));
  }
  
  /**
   * Resets the current path
   */
  public resetPath(): void {
    this.path = [];
    this.currentPathIndex = 0;
    this.pathCompleted = false;
    this.targetPosition = null;
  }
  
  /**
   * Gets the current path
   * @returns The current path
   */
  public getPath(): THREE.Vector3[] {
    return this.path;
  }
  
  /**
   * Gets the target position
   * @returns The target position
   */
  public getTargetPosition(): THREE.Vector3 | null {
    return this.targetPosition ? this.targetPosition.clone() : null;
  }
  
  /**
   * Checks if the path is completed
   * @returns True if the path is completed
   */
  public isPathCompleted(): boolean {
    return this.pathCompleted;
  }
  
  /**
   * Disposes of resources
   */
  public dispose(): void {
    this.resetPath();
    this.obstacles = [];
    this.navMesh = null;
  }
} 